import { NewsFilters } from '@api/api/news';
import { ScreenerFilter } from '@api/api/screener';
import { NewsFilter, NewsOption } from '@terminal/services/news/news.types';
import { Watchlist } from '@terminal/stores/use-watchlist/watchlist-types';
import { TFunction, t } from 'i18next';

import { NewsFiltersMapping } from '../../stores/use-news-url-store/useNewsUrlStore';
import { createDatePickerFilter } from '../get-search-filters/getSearchFilters';

export const WATCHLIST_FILTER_KEY = 'watchlist';

export interface ActiveFilterOptions {
  id: string;
  filter: string;
  options: Array<{
    key: string;
    name: string;
    value: { id: string };
  }>;
}

export const mapNewsFiltersToScreenerFilter = (
  newsFilters: NewsFilters
): ScreenerFilter[] =>
  newsFilters.map(filter => ({
    key: filter.key,
    name: filter.name,
    visible: true,
    searchable: false,
    category: 'Universe',
    locked: true,
    config: {
      name: filter.name,
      multiselect: true,
      options: filter.config.options.map(option => ({
        key: option.key,
        name: option.name,
        value: {
          [filter.key]: option.key,
        },
      })),
    },
  }));

export const mapWatchlistToNewsFilter = (lists: Watchlist[]): NewsFilter => ({
  name: t('news:sideAccordionFilters.watchlist'),
  key: WATCHLIST_FILTER_KEY,
  options: lists.map(l => ({
    isChecked: false,
    name: l.name,
    key: l.id,
    entities: l.entities.map(e => e.tag),
    visible: true,
  })),
});

export const mapNewsFilters = (data: NewsFilters): NewsFilter[] =>
  data.map(f => ({
    name: f.name,
    key: f.key,
    options: (f.config?.options || []).reduce<NewsOption[]>(
      (options, option) => {
        if (option.visible) {
          options.push({ ...option, isChecked: false });
        }

        return options;
      },
      []
    ),
  }));

interface CountFilterKeyCheckedProps {
  filters: NewsFilter[];
  filterKey: string;
}

export const countFilterKeyChecked = ({
  filters,
  filterKey,
}: CountFilterKeyCheckedProps) => {
  const filter = filters.find(f => f.key === filterKey);
  let some = false;
  let all = false;
  let count = 0;

  if (filter) {
    count = filter.options.filter(o => o.isChecked).length;
    some = count > 0;

    all = count === filter.options.length;
  }

  return { some, all, count };
};

export const createAccordionFilters = (
  filters: NewsFilters,
  watchlist: Watchlist[]
) => {
  const mappedFilters = mapNewsFilters(filters);
  const watchlistFilter = mapWatchlistToNewsFilter(watchlist);

  return [watchlistFilter, ...mappedFilters];
};

export const createScreenerFilters = (
  newsFilters: NewsFilters,
  t: TFunction
): ScreenerFilter[] => {
  if (!newsFilters.length) {
    return [];
  }

  const [providersFilter] = mapNewsFiltersToScreenerFilter(newsFilters);

  return [providersFilter, createDatePickerFilter(t)];
};

export const updateWatchlistOptions = (
  filter: NewsFilter,
  lists: Watchlist[]
): NewsOption[] => {
  const optionMap = new Map(filter.options.map(option => [option.key, option]));

  return lists.map(list => {
    const { isChecked = false, visible = true } = optionMap.get(list.id) ?? {};

    return {
      key: list.id,
      name: list.name,
      entities: list.entities.map(entity => entity.tag),
      isChecked,
      visible,
    };
  });
};

export const updateWatchlistFilter = (
  filters: NewsFilter[],
  lists: Watchlist[]
): NewsFilter[] =>
  filters.map(filter => {
    if (filter.key === WATCHLIST_FILTER_KEY) {
      const updatedOptions = updateWatchlistOptions(filter, lists);
      return { ...filter, options: updatedOptions };
    }

    return filter;
  });

export const applySelectionMapping = (
  base: NewsFilter[],
  mapping: Record<string, string[]>
): NewsFilter[] => {
  if (!base.length) {
    return base;
  }

  const selectedSets: Record<string, Set<string>> = Object.fromEntries(
    Object.entries(mapping ?? {}).map(([key, ids]) => [key, new Set(ids)])
  );

  return base.map(filter => {
    const selected = selectedSets[filter.key];

    if (!selected) {
      return filter;
    }

    return {
      ...filter,
      options: filter.options.map(option => ({
        ...option,
        isChecked: selected.has(option.key),
      })),
    };
  });
};

export const toggleSelectionMapping = ({
  current,
  filters,
  filterKey,
  optionKey,
}: {
  current: NewsFiltersMapping;
  filters: NewsFilter[];
  filterKey: string;
  optionKey?: string;
}): NewsFiltersMapping => {
  const nextMapping = { ...(current ?? {}) };
  const currentSelected = new Set(nextMapping[filterKey] ?? []);

  // when optionKey is not provided, we are toggling the entire filter
  if (!optionKey) {
    const source = filters.find(f => f.key === filterKey);

    if (!source) {
      return nextMapping;
    }

    const anyChecked = source.options.some(o => o.isChecked);
    const nextSelected = anyChecked ? [] : source.options.map(o => o.key);

    if (!nextSelected.length) {
      delete nextMapping[filterKey];
    } else {
      nextMapping[filterKey] = nextSelected;
    }

    return nextMapping;
  }

  if (currentSelected.has(optionKey)) {
    currentSelected.delete(optionKey);
  } else {
    currentSelected.add(optionKey);
  }

  if (!currentSelected.size) {
    delete nextMapping[filterKey];
  } else {
    nextMapping[filterKey] = Array.from(currentSelected);
  }

  return nextMapping;
};
